import React, { useState, useEffect, useCallback } from 'react';

/**
 * Trivia Game Component
 * Answer program-specific questions to unlock editorial insights about impact
 * Self-contained with inline styles, no external dependencies
 */

const TriviaGame = ({
  title = "Discover Impact",
  instructions = "Answer questions to reveal program insights",
  data = {
    questions: []
  },
  onComplete
}) => {
  // Game state
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [hasAnswered, setHasAnswered] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [insights, setInsights] = useState([]);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [hasSkipped, setHasSkipped] = useState(false);

  // Get current question
  const currentQuestion = data.questions?.[currentQuestionIndex];
  const totalQuestions = data.questions?.length || 0;

  // Check if game is complete
  useEffect(() => {
    if (currentQuestionIndex >= totalQuestions && totalQuestions > 0 && !isComplete) {
      setIsComplete(true);
      if (onComplete) {
        setTimeout(onComplete, 1500);
      }
    }
  }, [currentQuestionIndex, totalQuestions, isComplete, onComplete]);

  // Handle answer selection
  const handleAnswerSelect = useCallback((answerIndex) => {
    if (hasAnswered) return;

    setSelectedAnswer(answerIndex);
    setHasAnswered(true);

    const correct = answerIndex === currentQuestion.correctIndex;
    setIsCorrect(correct);

    if (correct) {
      setCorrectAnswers(prev => prev + 1);
    }

    // Add insight to list
    const insight = {
      question: currentQuestion.question,
      correctAnswer: currentQuestion.options[currentQuestion.correctIndex],
      userAnswer: currentQuestion.options[answerIndex],
      isCorrect: correct,
      revealText: correct ? currentQuestion.revealRight : currentQuestion.revealWrong,
      insightText: currentQuestion.insight
    };
    setInsights(prev => [...prev, insight]);
  }, [hasAnswered, currentQuestion]);

  // Move to next question
  const nextQuestion = useCallback(() => {
    setCurrentQuestionIndex(prev => prev + 1);
    setSelectedAnswer(null);
    setHasAnswered(false);
    setIsCorrect(false);
  }, []);

  // Reset game
  const resetGame = () => {
    setCurrentQuestionIndex(0);
    setSelectedAnswer(null);
    setHasAnswered(false);
    setIsCorrect(false);
    setInsights([]);
    setCorrectAnswers(0);
    setIsComplete(false);
    setHasSkipped(false);
  };

  // Skip to insights - reveal all answers
  const handleSkip = () => {
    const allInsights = data.questions.map(q => ({
      question: q.question,
      correctAnswer: q.options[q.correctIndex],
      userAnswer: q.options[q.correctIndex],
      isCorrect: true,
      revealText: q.revealRight || q.revealWrong,
      insightText: q.insight
    }));
    setInsights(allInsights);
    setIsComplete(true);
    setHasSkipped(true);
    if (onComplete) {
      setTimeout(onComplete, 1500);
    }
  };

  // Styles
  const styles = {
    container: {
      fontFamily: 'var(--sans, "DM Sans", sans-serif)',
      maxWidth: '800px',
      margin: '0 auto',
      padding: '20px',
      position: 'relative',
    },
    header: {
      textAlign: 'center',
      marginBottom: '30px',
    },
    title: {
      fontFamily: 'var(--serif, "DM Serif Display", serif)',
      fontSize: 'var(--text-2xl, 28px)',
      fontWeight: '600',
      color: 'var(--navy, #1B2A4A)',
      marginBottom: '10px',
    },
    instructions: {
      fontFamily: 'var(--sans, "DM Sans", sans-serif)',
      fontSize: 'var(--text-base, 15px)',
      color: 'var(--charcoal, #2D2D2D)',
      marginBottom: '20px',
    },
    progressContainer: {
      marginBottom: '30px',
    },
    progressDots: {
      display: 'flex',
      justifyContent: 'center',
      gap: '8px',
      marginBottom: '10px',
    },
    progressDot: {
      width: '10px',
      height: '10px',
      borderRadius: '50%',
      backgroundColor: 'var(--light, #F2EDE6)',
      transition: 'all var(--duration-base, 500ms) ease',
    },
    progressDotCurrent: {
      backgroundColor: 'var(--gold, #F7C948)',
      transform: 'scale(1.2)',
    },
    progressDotCompleted: {
      backgroundColor: 'var(--teal, #1A8A7D)',
    },
    progressText: {
      textAlign: 'center',
      fontSize: 'var(--text-sm, 14px)',
      color: 'var(--mid, #B8AFA4)',
      fontFamily: 'var(--sans, "DM Sans", sans-serif)',
    },
    questionCard: {
      backgroundColor: 'rgba(242, 237, 230, 0.3)',
      borderRadius: 'var(--radius-lg, 12px)',
      padding: '30px',
      marginBottom: '30px',
      animation: 'fadeIn 0.5s ease',
    },
    questionText: {
      fontSize: 'var(--text-lg, 18px)',
      fontWeight: '500',
      color: 'var(--charcoal, #2D2D2D)',
      marginBottom: '25px',
      lineHeight: '1.6',
      fontFamily: 'var(--sans, "DM Sans", sans-serif)',
    },
    optionsContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
    },
    optionButton: {
      padding: '15px 20px',
      borderRadius: 'var(--radius-md, 8px)',
      border: '2px solid var(--light, #F2EDE6)',
      backgroundColor: 'white',
      fontSize: 'var(--text-base, 15px)',
      textAlign: 'left',
      cursor: 'pointer',
      transition: 'all var(--duration-fast, 200ms) ease',
      fontFamily: 'var(--sans, "DM Sans", sans-serif)',
    },
    optionHover: {
      borderColor: 'var(--gold, #F7C948)',
      backgroundColor: 'var(--cream, #FFF9F0)',
      transform: 'translateX(4px)',
    },
    optionSelected: {
      borderColor: 'var(--gold, #F7C948)',
      backgroundColor: 'var(--cream, #FFF9F0)',
    },
    optionCorrect: {
      borderColor: 'var(--teal, #1A8A7D)',
      backgroundColor: 'rgba(26, 138, 125, 0.1)',
      animation: 'pulse 0.5s ease',
    },
    optionWrong: {
      borderColor: 'var(--rust, #C4652A)',
      backgroundColor: 'rgba(196, 101, 42, 0.1)',
    },
    optionDisabled: {
      cursor: 'not-allowed',
      opacity: '0.7',
    },
    revealCard: {
      marginTop: '20px',
      padding: '20px',
      borderRadius: '8px',
      animation: 'slideUp 0.5s ease',
    },
    revealCorrect: {
      backgroundColor: '#e8f5e9',
      border: '1px solid #66bb6a',
    },
    revealWrong: {
      backgroundColor: '#fff3e0',
      border: '1px solid #ffb74d',
    },
    revealText: {
      fontSize: '15px',
      color: '#333',
      lineHeight: '1.5',
      marginBottom: '15px',
    },
    insightBox: {
      marginTop: '15px',
      padding: '15px',
      backgroundColor: 'rgba(255, 255, 255, 0.5)',
      borderRadius: '6px',
    },
    insightLabel: {
      fontSize: '12px',
      fontWeight: '600',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      color: '#666',
      marginBottom: '8px',
    },
    insightText: {
      fontSize: '14px',
      color: '#333',
      lineHeight: '1.5',
    },
    nextButton: {
      marginTop: '20px',
      padding: '12px 24px',
      borderRadius: 'var(--radius-md, 8px)',
      border: '2px solid var(--gold, #F7C948)',
      backgroundColor: 'transparent',
      color: 'var(--gold, #F7C948)',
      fontSize: 'var(--text-base, 15px)',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all var(--duration-fast, 200ms) ease',
      fontFamily: 'var(--sans, "DM Sans", sans-serif)',
    },
    nextButtonHover: {
      backgroundColor: 'var(--gold, #F7C948)',
      color: 'white',
      transform: 'translateY(-1px)',
    },
    skipButton: {
      position: 'absolute',
      top: '10px',
      right: '10px',
      padding: '6px 12px',
      fontSize: 'var(--text-xs, 12px)',
      backgroundColor: 'transparent',
      color: 'var(--mid, #B8AFA4)',
      borderColor: 'var(--mid, #B8AFA4)',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderRadius: 'var(--radius-md, 8px)',
      fontFamily: 'var(--sans, "DM Sans", sans-serif)',
      cursor: 'pointer',
      transition: 'all var(--duration-fast, 200ms) ease',
      zIndex: 10,
    },
    insightsContainer: {
      marginTop: '40px',
    },
    insightsTitle: {
      fontSize: '22px',
      fontWeight: '600',
      marginBottom: '20px',
      color: '#333',
      textAlign: 'center',
    },
    insightItem: {
      marginBottom: '20px',
      padding: '20px',
      backgroundColor: '#f9f9f9',
      borderRadius: '8px',
      borderLeft: '4px solid #4a90e2',
    },
    insightQuestion: {
      fontSize: '16px',
      fontWeight: '500',
      marginBottom: '10px',
      color: '#333',
    },
    insightAnswer: {
      fontSize: '14px',
      color: '#666',
      marginBottom: '10px',
    },
    insightReveal: {
      fontSize: '14px',
      color: '#333',
      lineHeight: '1.5',
      paddingTop: '10px',
      borderTop: '1px solid #e0e0e0',
    },
    completionCard: {
      textAlign: 'center',
      padding: '40px',
      backgroundColor: '#f0f7ff',
      borderRadius: '12px',
      marginBottom: '30px',
    },
    completionTitle: {
      fontSize: 'var(--text-2xl, 28px)',
      color: 'var(--teal, #1A8A7D)',
      fontWeight: '600',
      marginBottom: '15px',
      fontFamily: 'var(--serif, "DM Serif Display", serif)',
    },
    completionStats: {
      fontSize: 'var(--text-base, 15px)',
      color: 'var(--charcoal, #2D2D2D)',
      fontFamily: 'var(--sans, "DM Sans", sans-serif)',
    },
    resetButton: {
      padding: '10px 20px',
      borderRadius: 'var(--radius-md, 8px)',
      border: '2px solid var(--light, #F2EDE6)',
      fontSize: 'var(--text-sm, 14px)',
      fontWeight: '500',
      cursor: 'pointer',
      backgroundColor: 'var(--light, #F2EDE6)',
      color: 'var(--charcoal, #2D2D2D)',
      transition: 'all var(--duration-fast, 200ms) ease',
      fontFamily: 'var(--sans, "DM Sans", sans-serif)',
    },
  };

  // Mobile responsive adjustments
  const isMobile = window.innerWidth < 640;
  if (isMobile) {
    styles.questionText.fontSize = '18px';
    styles.optionButton.fontSize = '14px';
    styles.optionButton.padding = '12px 16px';
    styles.header.paddingTop = '40px'; // Add padding to avoid button overlap
    styles.skipButton.fontSize = '11px';
    styles.skipButton.padding = '5px 10px';
  }

  // Don't render if no questions
  if (!data.questions || data.questions.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h2 style={styles.title}>{title}</h2>
          <p style={styles.instructions}>No questions available</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.02); }
          100% { transform: scale(1); }
        }
      `}</style>

      {/* Skip button */}
      {!isComplete && (
        <button
          style={styles.skipButton}
          onClick={handleSkip}
          aria-label="Skip quiz and view all insights"
        >
          Skip to Insights
        </button>
      )}

      <div style={styles.header}>
        <h2 style={styles.title}>{title}</h2>
        <p style={styles.instructions}>{instructions}</p>
      </div>

      {!isComplete ? (
        <>
          {/* Progress indicator */}
          <div style={styles.progressContainer}>
            <div style={styles.progressDots}>
              {[...Array(totalQuestions)].map((_, index) => (
                <div
                  key={index}
                  style={{
                    ...styles.progressDot,
                    ...(index === currentQuestionIndex ? styles.progressDotCurrent : {}),
                    ...(index < currentQuestionIndex ? styles.progressDotCompleted : {}),
                  }}
                />
              ))}
            </div>
            <div style={styles.progressText}>
              Question {currentQuestionIndex + 1} of {totalQuestions}
            </div>
          </div>

          {/* Question card */}
          {currentQuestion && (
            <div style={styles.questionCard}>
              <div style={styles.questionText}>
                {currentQuestion.question}
              </div>

              {/* Answer options */}
              <div style={styles.optionsContainer}>
                {currentQuestion.options.map((option, index) => {
                  const isSelected = selectedAnswer === index;
                  const isCorrectOption = index === currentQuestion.correctIndex;
                  const showCorrect = hasAnswered && isCorrectOption;
                  const showWrong = hasAnswered && isSelected && !isCorrectOption;

                  return (
                    <button
                      key={index}
                      style={{
                        ...styles.optionButton,
                        ...(isSelected ? styles.optionSelected : {}),
                        ...(showCorrect ? styles.optionCorrect : {}),
                        ...(showWrong ? styles.optionWrong : {}),
                        ...(hasAnswered ? styles.optionDisabled : {}),
                      }}
                      onClick={() => handleAnswerSelect(index)}
                      disabled={hasAnswered}
                      onMouseEnter={(e) => {
                        if (!hasAnswered) {
                          Object.assign(e.currentTarget.style, styles.optionHover);
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!hasAnswered && !isSelected) {
                          e.currentTarget.style.borderColor = '#e0e0e0';
                          e.currentTarget.style.backgroundColor = 'white';
                          e.currentTarget.style.transform = 'translateX(0)';
                        }
                      }}
                    >
                      {option}
                    </button>
                  );
                })}
              </div>

              {/* Reveal and insight */}
              {hasAnswered && (
                <div
                  style={{
                    ...styles.revealCard,
                    ...(isCorrect ? styles.revealCorrect : styles.revealWrong),
                  }}
                >
                  <div style={styles.revealText}>
                    {isCorrect ? currentQuestion.revealRight : currentQuestion.revealWrong}
                  </div>

                  {currentQuestion.insight && (
                    <div style={styles.insightBox}>
                      <div style={styles.insightLabel}>Insight</div>
                      <div style={styles.insightText}>
                        {currentQuestion.insight}
                      </div>
                    </div>
                  )}

                  <button
                    style={styles.nextButton}
                    onClick={nextQuestion}
                    onMouseEnter={(e) => {
                      Object.assign(e.currentTarget.style, styles.nextButtonHover);
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#4a90e2';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    {currentQuestionIndex < totalQuestions - 1 ? 'Next Question' : 'View Results'}
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <>
          {/* Completion message */}
          <div style={styles.completionCard}>
            <div style={styles.completionTitle}>
              {hasSkipped ? '✓ All Insights Revealed' : '🎉 Quiz Complete!'}
            </div>
            <div style={styles.completionStats}>
              {hasSkipped
                ? `Viewing insights from ${totalQuestions} questions`
                : `You answered ${correctAnswers} out of ${totalQuestions} questions correctly`}
            </div>
          </div>

          {/* All insights */}
          <div style={styles.insightsContainer}>
            <h3 style={styles.insightsTitle}>Program Impact Insights</h3>
            {insights.map((insight, index) => (
              <div key={index} style={styles.insightItem}>
                <div style={styles.insightQuestion}>
                  {insight.question}
                </div>
                <div style={styles.insightAnswer}>
                  Answer: {insight.correctAnswer}
                  {!insight.isCorrect && ` (You answered: ${insight.userAnswer})`}
                </div>
                <div style={styles.insightReveal}>
                  {insight.insightText}
                </div>
              </div>
            ))}
          </div>

          {/* Reset button */}
          <div style={{ textAlign: 'center', marginTop: '30px' }}>
            <button
              style={styles.resetButton}
              onClick={resetGame}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#e0e0e0';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#f0f0f0';
              }}
            >
              Play Again
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default TriviaGame;